import { faPause, faPlay, faUndoAlt, faVolumeMute, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Blurhash } from 'react-blurhash';
import { Button, ButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { Video } from '../Video/Video';
import { VideoCard } from '../VideoCard/VideoCard';
import styles from './Billboard.module.scss';

const BillboardComponent = ({
  video: billboard,
  play: billboardPlay,
  setPlay: setBillboardPlay,
  wrapper,
}: {
  video: { [p: string]: any };
  play: boolean;
  setPlay: Dispatch<SetStateAction<boolean>>;
  wrapper: MutableRefObject<HTMLDivElement>;
}) => {
  const videoElementRef = useRef<HTMLVideoElement>();

  const { staticData } = useFirebase();
  const { windowWidth } = useWindowSize();
  const { t } = useTranslation();

  const [billboardAudio, setBillboardAudio] = useState(false);
  const [billboardEnded, setBillboardEnded] = useState(false);
  const [hasBillboardEndedOnce, setHasBillboardEndedOnce] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);

  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const history = useHistory();

  useLayoutEffect(() => {
    if (!wrapper.current) return;
    const width = wrapper.current.clientWidth;
    const height = width / 1.7777;
    setCanvasSize({ width, height });
  }, [windowWidth, wrapper.current]);

  useEffect(() => {
    if (!videoElementRef.current) return;

    try {
      if (billboardPlay) videoElementRef.current.play().catch(console.error);
      else videoElementRef.current.pause();
    } catch {}
  }, [billboardPlay]);

  const underlyingBillboardRef = useRef<HTMLDivElement>();

  const openBillboard = () => {
    if (billboard.redirectToExternal) return window.open(billboard.redirectToExternal, '_blank');
    if (billboard.redirectTo) return history.push(billboard.redirectTo);
    underlyingBillboardRef.current.click();
  };

  const billboardOnPlay = () => {
    if (hasBillboardEndedOnce) setHasBillboardEndedOnce(false);
    setBillboardEnded(false);
  };

  const billboardOnLoadedMetadata = () => {
    setPosterLoaded(true);
  };

  const billboardOnEnded = () => {
    if (!hasBillboardEndedOnce) setHasBillboardEndedOnce(true);
    setBillboardPlay(false);
    setBillboardEnded(true);
    videoElementRef.current.currentTime = 0;
    videoElementRef.current.src = null;
    videoElementRef.current.src = billboard.trailer;
  };

  return (
    <>
      {!!billboard.thumbnail_hash && canvasSize.width && staticData.ENABLE_BLURHASH && (
        <Blurhash
          className={`${styles.blurhashWrapper} ${posterLoaded ? styles.blurhashWrapperHidden : ''}`}
          hash={billboard.thumbnail_hash}
          width={canvasSize.width}
          height={canvasSize.height}
        />
      )}
      <Video
        poster={billboard.thumbnail}
        muted={!billboardAudio}
        preload={'metadata'}
        innerRef={videoElementRef}
        src={billboard.trailer}
        className={styles.clickableVideo}
        onClick={openBillboard}
        onPlay={billboardOnPlay}
        onEnded={billboardOnEnded}
        onLoadedMetadata={billboardOnLoadedMetadata}
      />
      <div className={'d-none'}>
        <VideoCard
          hidden={true}
          video={billboard}
          keepWatching={false}
          innerRef={underlyingBillboardRef}
          onDetailsOpen={() => {
            if (billboardPlay) setBillboardPlay(false);
          }}
        />
      </div>
      {!billboard.id?.startsWith('00000000-') && (
        <Button className={`tootor-button tootor-button--primary ${styles.watchNow}`} onClick={openBillboard}>
          <FontAwesomeIcon icon={faPlay} fixedWidth /> {t('labels.guardaOra')}
        </Button>
      )}
      <div className={styles.billboardActions}>
        <ButtonGroup>
           <Button className="tootor-button tootor-button--basic" onClick={() => setBillboardPlay((current) => !current)}>
            <FontAwesomeIcon icon={billboardEnded ? faUndoAlt : billboardPlay ? faPause : faPlay} fixedWidth={true} />
          </Button>
           <Button className="tootor-button tootor-button--basic" onClick={() => setBillboardAudio((current) => !current)}>
            <FontAwesomeIcon icon={billboardAudio ? faVolumeUp : faVolumeMute} fixedWidth={true} />
          </Button>
        </ButtonGroup>
      </div>
      <div className={styles.bottomShader} />
    </>
  );
};

export { BillboardComponent as Billboard };
